import React, { useState, useEffect, useMemo } from 'react';
import { FiRefreshCw, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { IconType } from 'react-icons';
import { dashboardService } from '../api/services/dashboardService';
import { AppSession as ApiAppSession, PaginatedResponse, TableCountsResponse } from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';

interface AppSession {
  id: number;
  appId: string;
  playerId: string;
  startedAt: string;
  endedAt: string;
  startedAtOriginal: string | null;
  endedAtOriginal: string | null;
  durationInSeconds: number;
}

type SortColumn = 'id' | 'appId' | 'playerId' | 'startedAt' | 'endedAt' | 'durationInSeconds';
type SortDirection = 'asc' | 'desc';

const AppSessions: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 25;
  const [sessions, setSessions] = useState<AppSession[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [tableCounts, setTableCounts] = useState<TableCountsResponse | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Format duration
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Fetch app sessions from API
  const fetchAppSessions = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response: PaginatedResponse<ApiAppSession> = await dashboardService.getAppSessionDetails({
        page,
      });

      // Transform API data to local format
      const transformedSessions: AppSession[] = response.data.map((sessionDetail) => ({
        id: sessionDetail.app_session_id,
        appId: sessionDetail.app_id,
        playerId: sessionDetail.player_id,
        startedAt: formatDate(sessionDetail.started_at),
        endedAt: formatDate(sessionDetail.ended_at),
        startedAtOriginal: sessionDetail.started_at,
        endedAtOriginal: sessionDetail.ended_at,
        durationInSeconds: sessionDetail.duration_in_seconds,
      }));

      setSessions(transformedSessions);
      setPagination({
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      });
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching app sessions:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch table counts for stats
  const fetchTableCounts = async () => {
    try {
      const response = await dashboardService.getTableCounts();
      setTableCounts(response);
    } catch (err: any) {
      console.error('Error fetching table counts:', err);
    }
  };

  useEffect(() => {
    fetchAppSessions(currentPage);
    fetchTableCounts();
  }, [currentPage]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchAppSessions(currentPage);
    fetchTableCounts();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
    setCurrentPage(page);
    }
  };

  // Calculate percentage change
  const calculateChange = (current: number, previous: number): string => {
    if (previous === 0) return '+0%';
    const change = ((current - previous) / previous) * 100;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get daily change from metrics (latest month vs previous month)
  // Get daily change from metrics (daily growth percentage)
  const getDailyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get weekly change from metrics (weekly growth percentage)
  const getWeeklyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_weekly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get monthly change from metrics (monthly growth percentage)
  const getMonthlyChange = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_monthly`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Get today's count by metric type
  const getTodayCount = (metricType: string): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === metricType);
    return count?.total_today || 0;
  };

  // Get today's average duration from total_counts
  const getAvgDurationToday = (metricType: string): number => {
    if (!tableCounts) return 0;
    const count = tableCounts.total_counts.find(tc => tc.metric_type === metricType);
    return count?.avg_duration_today || 0;
  };

  // Get daily change from Daily metrics (daily growth percentage)
  const getDailyValue = (metricType: string): string => {
    if (!tableCounts || !tableCounts.metrics) return '+0%';
    const key = `${metricType}_daily`;
    const metric = tableCounts.metrics[key];
    if (!metric) return '+0%';
    const change = metric.growth_percentage;
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  // Handle sort
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort sessions
  const sortedSessions = useMemo(() => {
    if (!sortColumn) return sessions;

    return [...sessions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'appId':
          aValue = a.appId.toLowerCase();
          bValue = b.appId.toLowerCase();
          break;
        case 'playerId':
          aValue = a.playerId.toLowerCase();
          bValue = b.playerId.toLowerCase();
          break;
        case 'startedAt':
          aValue = a.startedAtOriginal ? new Date(a.startedAtOriginal).getTime() : 0;
          bValue = b.startedAtOriginal ? new Date(b.startedAtOriginal).getTime() : 0;
          break;
        case 'endedAt':
          aValue = a.endedAtOriginal ? new Date(a.endedAtOriginal).getTime() : 0;
          bValue = b.endedAtOriginal ? new Date(b.endedAtOriginal).getTime() : 0;
          break;
        case 'durationInSeconds':
          aValue = a.durationInSeconds;
          bValue = b.durationInSeconds;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sessions, sortColumn, sortDirection]);

  // Calculate stats from API data
  const totalDuration = sessions.reduce((sum, s) => sum + s.durationInSeconds, 0);
  const avgDuration = sessions.length > 0 ? Math.round(totalDuration / sessions.length) : 0;

  const stats = [
    { 
      label: 'Toplam Oturum', 
      value: pagination.total.toLocaleString('tr-TR'), 
      change: getDailyChange('app_session'), 
      weeklyChange: getWeeklyChange('app_session'),
      monthlyChange: getMonthlyChange('app_session'),
      color: 'blue' 
    },
    { 
      label: 'Oturum Sayısı', 
      value: getTodayCount('app_session').toLocaleString('tr-TR'), 
      color: 'green' 
    },
    { 
      label: 'Ortalama Süre', 
      value: formatDuration(getAvgDurationToday('app_session')), 
      color: 'purple' 
    },
  ];

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Uygulama Oturumları
        </h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {stats.map((stat, index) => (
          <div key={index} className={`relative glass-strong rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 p-6 border overflow-hidden group transform hover:-translate-y-1 animate-fade-in ${
            stat.color === 'blue' ? 'hover:border-blue-300 dark:hover:border-blue-600' :
            stat.color === 'green' ? 'hover:border-green-300 dark:hover:border-green-600' :
            stat.color === 'purple' ? 'hover:border-purple-300 dark:hover:border-purple-600' :
            'hover:border-indigo-300 dark:hover:border-indigo-600'
          }`} style={{ animationDelay: `${index * 100}ms` }}>
            {/* Animated gradient background */}
            <div className={`absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-30 group-hover:opacity-50 group-hover:scale-150 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              stat.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
              'bg-indigo-100 dark:bg-indigo-900/30'
            }`}></div>
            <div className={`absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-all duration-700 ${
              stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
              stat.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              stat.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
              'bg-indigo-100 dark:bg-indigo-900/30'
            }`}></div>
            
            {/* Shimmer effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <div className="absolute inset-0 shimmer"></div>
            </div>
            
            <div className="relative z-10">
              <p className="text-sm font-bold text-slate-600 dark:text-slate-300 mb-3 uppercase tracking-wider">{stat.label}</p>
              <p className={`text-5xl font-extrabold mb-4 bg-gradient-to-r ${
                stat.color === 'blue' ? 'from-blue-600 via-blue-500 to-blue-400' :
                stat.color === 'green' ? 'from-green-600 via-green-500 to-green-400' :
                stat.color === 'purple' ? 'from-purple-600 via-purple-500 to-purple-400' :
                'from-indigo-600 via-indigo-500 to-indigo-400'
              } bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300`}>
                {stat.value}
              </p>
              {stat.label === 'Toplam Oturum' && (
                <div className="flex flex-col gap-3 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                  {stat.change && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Günlük</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.change.startsWith('+') || stat.change.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.change}
                      </span>
                    </div>
                  )}
                  {stat.weeklyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Haftalık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.weeklyChange.startsWith('+') || stat.weeklyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.weeklyChange}
                      </span>
                    </div>
                  )}
                  {stat.monthlyChange && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Aylık</span>
                      <span className={`text-sm font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all duration-300 ${
                        stat.monthlyChange.startsWith('+') || stat.monthlyChange.startsWith('0')
                          ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                          : 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                      }`}>
                        {stat.monthlyChange}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Sessions Table */}
      <div className="glass-strong rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden border transform hover:scale-[1.01] animate-fade-in">
        <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-indigo-50/50 via-purple-50/50 to-pink-50/50 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20 backdrop-blur-sm flex items-center justify-between">
          <h2 className="text-xl font-bold gradient-text">App Sessions</h2>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="flex items-center gap-2 px-6 py-3 text-sm font-semibold text-gray-800 dark:text-white bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 transform disabled:hover:scale-100"
          >
            {renderIcon(FiRefreshCw, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
            <span>Yenile</span>
          </button>
        </div>
        {error && (
          <div className="px-6 py-4 glass bg-gradient-to-r from-red-50/80 to-rose-50/80 dark:from-red-900/30 dark:to-rose-900/30 border-b border-red-200/50 dark:border-red-800/50">
            <div className="flex items-center gap-3 text-red-700 dark:text-red-300">
              <span className="text-xl">⚠️</span>
              <span className="font-semibold">{error}</span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-gray-700 dark:to-gray-800">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center gap-2">
                    <span>ID</span>
                    {sortColumn === 'id' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('appId')}
                >
                  <div className="flex items-center gap-2">
                    <span>App ID</span>
                    {sortColumn === 'appId' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('playerId')}
                >
                  <div className="flex items-center gap-2">
                    <span>Player ID</span>
                    {sortColumn === 'playerId' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('startedAt')}
                >
                  <div className="flex items-center gap-2">
                    <span>Başlangıç</span>
                    {sortColumn === 'startedAt' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('endedAt')}
                >
                  <div className="flex items-center gap-2">
                    <span>Bitiş</span>
                    {sortColumn === 'endedAt' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('durationInSeconds')}
                >
                  <div className="flex items-center gap-2">
                    <span>Süre (s)</span>
                    {sortColumn === 'durationInSeconds' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {sortedSessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    Uygulama oturumu bulunamadı
                  </td>
                </tr>
              ) : (
                sortedSessions.map((session) => (
                <tr key={session.id} className="hover:bg-gradient-to-r hover:from-indigo-50/50 hover:to-purple-50/50 dark:hover:from-indigo-900/20 dark:hover:to-purple-900/20 transition-all duration-300 border-b border-gray-100/50 dark:border-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">{session.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.appId}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-600 dark:text-slate-300">{session.playerId}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.startedAt}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.endedAt}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">{session.durationInSeconds}s</td>
                </tr>
                ))
              )}
            </tbody>
          </table>
          )}
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Toplam {pagination.total} kayıttan {sessions.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}-{Math.min(currentPage * itemsPerPage, pagination.total)} arası gösteriliyor
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Önceki
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(pagination.totalPages, 10) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 10) {
                    pageNum = i + 1;
                  } else if (currentPage <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 4) {
                    pageNum = pagination.totalPages - 9 + i;
                  } else {
                    pageNum = currentPage - 5 + i;
                  }
                  return (
                  <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                    className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 ${
                        currentPage === pageNum
                        ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-500 dark:via-purple-500 dark:to-pink-500 text-white shadow-lg scale-110'
                        : 'text-slate-700 dark:text-slate-300 glass border border-gray-300/50 dark:border-gray-600/50 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 hover:scale-105 transform shadow-sm hover:shadow-md'
                    }`}
                  >
                      {pageNum}
                  </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
                className="px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 glass rounded-xl hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 dark:hover:from-indigo-900/30 dark:hover:to-purple-900/30 border border-gray-300/50 dark:border-gray-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 transform disabled:hover:scale-100 shadow-sm hover:shadow-md"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppSessions;

